import { useAuthStore } from '@/stores/authStore';
import { canTrackXPixel, initializeXPixel, X_REGISTRATION_RECEIPT_PREFIX } from './x-pixel';

// Created in the brand account's Events Manager on 2026-11-02. Public tag IDs.
export const X_CONVERSION_EVENTS = {
  registration: 'tw-ri7yw-ricz8',
  checkout: 'tw-ri7yw-ricz9',
  purchase: 'tw-ri7yw-riczb',
} as const;
const STARTED_AT = Date.parse('2026-11-02T09:45:00Z');
const PENDING_KEY = 'dobidobi.x-pending.v1';
const SENT_PREFIX = 'dobidobi.x-sent.v1:';
type Kind = keyof typeof X_CONVERSION_EVENTS;
type Pending = { owner: string; kind: Kind; conversionId: string; createdAt: number; value?: number; currency?: string };
const memory = new Map<string, Pending>();
const sent = new Set<string>();
const running = new Set<string>();

export function captureXConversionOwner(): string | undefined {
  const state = useAuthStore.getState();
  return state.isAuthenticated && state.user?.id && !state.user.isAdmin ? state.user.id : undefined;
}

function eligible(owner: string | undefined): owner is string {
  if (!owner || captureXConversionOwner() !== owner || !canTrackXPixel()) return false;
  const query = new URLSearchParams(window.location.search);
  return query.get('utm_source') !== 'qa' && query.get('utm_medium') !== 'validation';
}

function persistPending(): void {
  try { window.localStorage.setItem(PENDING_KEY, JSON.stringify([...memory.values()].slice(-50))); }
  catch { /* Memory remains usable when storage is restricted. */ }
}

function restorePending(): void {
  try {
    const rows: unknown = JSON.parse(window.localStorage.getItem(PENDING_KEY) || '[]');
    if (!Array.isArray(rows)) return;
    for (const row of rows.slice(-50)) {
      if (row && typeof row.owner === 'string' && Object.hasOwn(X_CONVERSION_EVENTS, row.kind)
        && typeof row.conversionId === 'string' && /^[a-z0-9_]{16,90}$/.test(row.conversionId)
        && Number.isFinite(row.createdAt) && Date.now() - row.createdAt < 86400000) {
        memory.set(`${row.kind}:${row.conversionId}`, row);
      }
    }
  } catch { /* Invalid or unavailable storage cannot affect the product. */ }
}

async function waitForTag(): Promise<boolean> {
  initializeXPixel();
  const target = window as Window & { twq?: { (...args: unknown[]): void; exe?: unknown } };
  if (target.twq?.exe) return true;
  const script = document.getElementById('dobidobi-x-pixel');
  if (!script) return false;
  return new Promise(resolve => {
    const finish = (ready: boolean) => {
      clearTimeout(timer);
      script.removeEventListener('load', loaded);
      script.removeEventListener('error', failed);
      resolve(ready);
    };
    const loaded = () => finish(Boolean(target.twq?.exe));
    const failed = () => finish(false);
    const timer = setTimeout(failed, 5000);
    script.addEventListener('load', loaded, { once: true });
    script.addEventListener('error', failed, { once: true });
  });
}

async function deliver(event: Pending): Promise<void> {
  const key = `${event.kind}:${event.conversionId}`;
  if (!eligible(event.owner) || running.has(key)) return;
  try {
    if (sent.has(key) || window.localStorage.getItem(SENT_PREFIX + key)) {
      if (event.kind === 'registration') {
        try { window.localStorage.removeItem(X_REGISTRATION_RECEIPT_PREFIX + event.owner); } catch { /* Optional cleanup. */ }
      }
      memory.delete(key); persistPending(); return;
    }
  } catch { /* In-memory dedupe is retained. */ }
  running.add(key);
  try {
    if (!await waitForTag() || !eligible(event.owner)) return;
    const payload: Record<string, unknown> = { conversion_id: event.conversionId };
    if (event.kind === 'purchase') {
      if (typeof event.value !== 'number' || !Number.isFinite(event.value) || event.value < 0
        || typeof event.currency !== 'string' || !/^[A-Z]{3}$/.test(event.currency)) return;
      payload.value = event.value;
      payload.currency = event.currency;
    }
    const target = window as Window & { twq?: (...args: unknown[]) => void };
    if (!target.twq) return;
    target.twq('event', X_CONVERSION_EVENTS[event.kind], payload);
    sent.add(key);
    try { window.localStorage.setItem(SENT_PREFIX + key, '1'); } catch { /* Memory dedupes. */ }
    if (event.kind === 'registration') {
      try { window.localStorage.removeItem(X_REGISTRATION_RECEIPT_PREFIX + event.owner); } catch { /* Sent marker dedupes. */ }
    }
    memory.delete(key); persistPending();
  } catch { /* A blocked tag never blocks auth or commerce. Retain for resume. */ }
  finally { running.delete(key); }
}

function enqueue(event: Pending): void {
  if (!eligible(event.owner)) return;
  restorePending();
  memory.set(`${event.kind}:${event.conversionId}`, event);
  persistPending();
  void deliver(event);
}

async function opaqueId(kind: Kind, identity: string): Promise<string> {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`${kind}:${identity}`));
  return Array.from(new Uint8Array(bytes), byte => byte.toString(16).padStart(2, '0')).join('');
}

export function trackXRegistration(owner: string, registrationEventId: string): void {
  if (!eligible(owner) || !/^[0-9a-f]{32}$/.test(registrationEventId)) return;
  enqueue({ owner, kind: 'registration', conversionId: registrationEventId, createdAt: Date.now() });
}

export function trackXCheckout(owner: string | undefined, orderId: string): void {
  if (!eligible(owner) || !orderId) return;
  void opaqueId('checkout', orderId).then(conversionId =>
    enqueue({ owner, kind: 'checkout', conversionId, createdAt: Date.now() }),
  ).catch(() => {});
}

export function trackXPurchase(owner: string | undefined, orderId: string, receipt: {
  success?: boolean; status?: string; paid_at?: string | null; amount_yuan?: number; currency?: string;
}): void {
  if (!eligible(owner) || !orderId || receipt.success !== true || receipt.status !== 'paid') return;
  const paidAt = Date.parse(receipt.paid_at || '');
  if (!Number.isFinite(paidAt) || paidAt < STARTED_AT || paidAt > Date.now() + 60000) return;
  const value = receipt.amount_yuan, currency = receipt.currency;
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0
    || typeof currency !== 'string' || !/^[A-Z]{3}$/.test(currency)) return;
  void opaqueId('purchase', orderId).then(conversionId =>
    enqueue({ owner, kind: 'purchase', conversionId, createdAt: Date.now(), value, currency }),
  ).catch(() => {});
}

/** Retry receipts across navigation/resume; never turn an ordinary login into signup. */
export function flushXConversions(): void {
  const owner = captureXConversionOwner();
  if (!eligible(owner)) return;
  restorePending();
  try {
    const receipt = JSON.parse(window.localStorage.getItem(X_REGISTRATION_RECEIPT_PREFIX + owner) || 'null');
    if (receipt && /^[0-9a-f]{32}$/.test(receipt.nonce)
      && Number.isFinite(receipt.createdAt) && Date.now() - receipt.createdAt < 86400000) {
      memory.set(`registration:${receipt.nonce}`, {
        owner, kind: 'registration', conversionId: receipt.nonce, createdAt: receipt.createdAt,
      });
    }
  } catch { /* Optional receipt may be absent or storage unavailable. */ }
  for (const event of memory.values()) {
    if (Date.now() - event.createdAt >= 86400000) { memory.delete(`${event.kind}:${event.conversionId}`); continue; }
    if (event.owner === owner) void deliver(event);
  }
}
